import { useId } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { ProfileFieldDef } from '@natalan/capehost-contracts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { emptyGroupItem, fieldApplies, type InputFields } from '@/lib/profile'

const CONTROL_CLASS =
  'w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50'

interface FieldEditorProps {
  field: ProfileFieldDef
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  /** Field-level error from the contracts' save schema, keyed by path. */
  error?: string
  errorsFor: (key: string) => string | undefined
  sectionFieldsOf: (sectionId: string) => Record<string, unknown> | undefined
}

function Label({ htmlFor, field }: { htmlFor?: string; field: ProfileFieldDef }) {
  const content = (
    <>
      {field.label}
      {field.required && <span className="ml-1 text-xs font-normal text-textMuted">required</span>}
    </>
  )
  return htmlFor ? (
    <label htmlFor={htmlFor} className="text-sm font-medium text-text">
      {content}
    </label>
  ) : (
    <span className="text-sm font-medium text-text">{content}</span>
  )
}

function Helper({ id, field }: { id: string; field: ProfileFieldDef }) {
  return field.helper ? (
    <p id={id} className="text-xs text-textMuted">
      {field.helper}
    </p>
  ) : null
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-xs text-danger">{message}</p> : null
}

/**
 * One profile question as an input, built from its declaration: text,
 * number, yes/no, choice, multiple choice, month, or a repeating group of
 * further questions. Unanswered is `null` everywhere, never a default.
 */
export function ProfileFieldEditor({
  field,
  value,
  onChange,
  disabled,
  error,
  errorsFor,
  sectionFieldsOf,
}: FieldEditorProps) {
  const id = useId()
  const helperId = `${id}-helper`
  const describedBy = field.helper ? helperId : undefined

  switch (field.kind) {
    case 'text':
      return (
        <div className="space-y-1">
          <Label htmlFor={id} field={field} />
          {field.multiline ? (
            <textarea
              id={id}
              rows={3}
              className={`${CONTROL_CLASS} resize-y`}
              value={typeof value === 'string' ? value : ''}
              onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
              aria-describedby={describedBy}
              disabled={disabled}
              autoComplete="off"
            />
          ) : (
            <Input
              id={id}
              type={field.format === 'email' ? 'email' : 'text'}
              inputMode={field.format === 'phone' ? 'tel' : undefined}
              value={typeof value === 'string' ? value : ''}
              onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
              aria-describedby={describedBy}
              disabled={disabled}
              autoComplete="off"
              className={field.sensitive ? 'font-mono' : undefined}
            />
          )}
          <Helper id={helperId} field={field} />
          <FieldError message={error} />
        </div>
      )
    case 'number':
      return (
        <div className="space-y-1">
          <Label htmlFor={id} field={field} />
          <div className="flex items-center gap-2">
            <Input
              id={id}
              type="number"
              min={field.min}
              max={field.max}
              step={field.decimal ? 'any' : 1}
              value={typeof value === 'number' ? String(value) : ''}
              onChange={e => {
                const raw = e.target.value
                onChange(raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw))
              }}
              aria-describedby={describedBy}
              disabled={disabled}
              className="max-w-[12rem]"
            />
            {field.unit && <span className="text-sm text-textMuted">{field.unit}</span>}
          </div>
          <Helper id={helperId} field={field} />
          <FieldError message={error} />
        </div>
      )
    case 'boolean':
      return (
        <div className="space-y-1">
          <Label htmlFor={id} field={field} />
          <select
            id={id}
            className={`${CONTROL_CLASS} h-10 max-w-[12rem]`}
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={e =>
              onChange(e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)
            }
            aria-describedby={describedBy}
            disabled={disabled}
          >
            <option value="">Not answered</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          <Helper id={helperId} field={field} />
          <FieldError message={error} />
        </div>
      )
    case 'choice':
      return (
        <div className="space-y-1">
          <Label htmlFor={id} field={field} />
          <select
            id={id}
            className={`${CONTROL_CLASS} h-10`}
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
            aria-describedby={describedBy}
            disabled={disabled}
          >
            <option value="">Not answered</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {/* A stored answer whose option was retired stays visible, so it can be changed. */}
            {typeof value === 'string' &&
              value !== '' &&
              !field.options.some(option => option.value === value) && (
                <option value={value}>{value} (no longer an option)</option>
              )}
          </select>
          <Helper id={helperId} field={field} />
          <FieldError message={error} />
        </div>
      )
    case 'multi_choice': {
      const selected = Array.isArray(value) ? (value as string[]) : null
      const retired = (selected ?? []).filter(v => !field.options.some(o => o.value === v))
      const toggle = (option: string, checked: boolean) => {
        const current = selected ?? []
        onChange(checked ? [...current, option] : current.filter(v => v !== option))
      }
      return (
        <fieldset className="space-y-1" aria-describedby={describedBy}>
          <legend className="text-sm font-medium text-text">
            {field.label}
            {field.required && (
              <span className="ml-1 text-xs font-normal text-textMuted">required</span>
            )}
          </legend>
          <Helper id={helperId} field={field} />
          <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
            {field.options.map(option => (
              <label key={option.value} className="flex min-h-[36px] items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected?.includes(option.value) ?? false}
                  onChange={e => toggle(option.value, e.target.checked)}
                  disabled={disabled}
                />
                {option.label}
              </label>
            ))}
            {retired.map(v => (
              <label
                key={v}
                className="flex min-h-[36px] items-center gap-2 text-sm text-textMuted"
              >
                <input
                  type="checkbox"
                  checked
                  onChange={e => toggle(v, e.target.checked)}
                  disabled={disabled}
                />
                {v} (no longer an option)
              </label>
            ))}
          </div>
          <p className="text-xs text-textMuted">
            {selected === null
              ? 'Not answered.'
              : selected.length === 0
                ? 'Answered: none of these.'
                : null}{' '}
            {selected === null ? (
              <button
                type="button"
                className="text-mint hover:underline"
                onClick={() => onChange([])}
                disabled={disabled}
              >
                Answer &quot;none of these&quot;
              </button>
            ) : (
              <button
                type="button"
                className="text-mint hover:underline"
                onClick={() => onChange(null)}
                disabled={disabled}
              >
                Clear the answer
              </button>
            )}
          </p>
          <FieldError message={error} />
        </fieldset>
      )
    }
    case 'month':
      return (
        <div className="space-y-1">
          <Label htmlFor={id} field={field} />
          <Input
            id={id}
            type="month"
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
            aria-describedby={describedBy}
            disabled={disabled}
            className="max-w-[12rem]"
          />
          <Helper id={helperId} field={field} />
          <FieldError message={error} />
        </div>
      )
    case 'group': {
      const items = Array.isArray(value) ? (value as (InputFields & { id: string })[]) : null
      const setItem = (index: number, next: InputFields & { id: string }) =>
        onChange(items!.map((item, i) => (i === index ? next : item)))
      return (
        <fieldset className="space-y-2" aria-describedby={describedBy}>
          <legend className="text-sm font-medium text-text">
            {field.label}
            {field.required && (
              <span className="ml-1 text-xs font-normal text-textMuted">required</span>
            )}
          </legend>
          <Helper id={helperId} field={field} />
          {items === null && (
            <p className="text-xs text-textMuted">
              Not answered.{' '}
              <button
                type="button"
                className="text-mint hover:underline"
                onClick={() => onChange([])}
                disabled={disabled}
              >
                Answer &quot;none&quot;
              </button>
            </p>
          )}
          {items?.length === 0 && <p className="text-xs text-textMuted">Answered: none.</p>}
          {items?.map((item, index) => (
            <div key={item.id} className="space-y-3 rounded-card border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-text">
                  {field.item_label} {index + 1}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(items.filter((_, i) => i !== index))}
                  disabled={disabled}
                  aria-label={`Remove ${field.item_label.toLowerCase()} ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              {field.fields
                .filter(sub => fieldApplies(sub, item, sectionFieldsOf))
                .map(sub => (
                  <ProfileFieldEditor
                    key={sub.key}
                    field={sub}
                    value={item[sub.key]}
                    onChange={next => setItem(index, { ...item, [sub.key]: next })}
                    disabled={disabled}
                    error={errorsFor(`${index}.${sub.key}`)}
                    errorsFor={key => errorsFor(`${index}.${sub.key}.${key}`)}
                    sectionFieldsOf={sectionFieldsOf}
                  />
                ))}
            </div>
          ))}
          {(items?.length ?? 0) < field.max_items && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onChange([...(items ?? []), emptyGroupItem(field.fields)])}
              disabled={disabled}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              Add {field.item_label.toLowerCase()}
            </Button>
          )}
          <FieldError message={error} />
        </fieldset>
      )
    }
  }
}
